"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslations } from "next-intl";
import * as z from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState } from "react";
import { toast } from "sonner";

export type FormValues = {
  fullName: string;
  email: string;
  subject: "contact" | "feedback";
  message: string;
};

interface ContactFormProps {
  onSubmit: (values: FormValues) => Promise<{ success: boolean }>;
}

export function ContactForm({ onSubmit }: ContactFormProps) {
  const t = useTranslations("Contact.form");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formSchema = z.object({
    fullName: z.string().min(3, {
      message: t("errors.nameMin"),
    }),
    email: z.string().email({
      message: t("errors.emailInvalid"),
    }),
    subject: z.enum(["contact", "feedback"]),
    message: z.string().min(10, {
      message: t("errors.messageMin"),
    }),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      email: "",
      subject: "contact",
      message: "",
    },
  });

  async function handleSubmit(values: FormValues) {
    try {
      setIsSubmitting(true);
      const result = await onSubmit(values);

      if (!result.success) throw new Error();

      toast.success(t("success"));
      form.reset();
    } catch (error) {
      toast.error(t("error"));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-base sm:text-sm">{t("fullName")}</FormLabel>
              <FormControl>
                <Input className="text-base sm:text-sm" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-base sm:text-sm">{t("email")}</FormLabel>
              <FormControl>
                <Input type="email" className="text-base sm:text-sm" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="subject"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-base sm:text-sm">{t("subject")}</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="text-base sm:text-sm">
                    <SelectValue placeholder={t("selectSubject")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem className="text-base sm:text-sm" value="contact">{t("subjectContact")}</SelectItem>
                  <SelectItem className="text-base sm:text-sm" value="feedback">{t("subjectFeedback")}</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-base sm:text-sm">{t("message")}</FormLabel>
              <FormControl>
                <Textarea rows={5} className="text-base sm:text-sm" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button 
          type="submit" 
          className="w-full max-w-[200px] mx-auto" 
          disabled={isSubmitting}
        >
          {isSubmitting ? t("sending") : t("send")}
        </Button>
      </form>
    </Form>
  );
} 